import { CustomEpic, PayloadAction } from 'types/types';
import { from, of } from 'rxjs';
import { catchError, exhaustMap, map, switchMap } from 'rxjs/operators';
import { ofType } from 'redux-observable';
import toast from 'react-hot-toast';
import {
  fetchAppFiles,
  requestFormCancel,
  requestFormFailure,
  requestFormSuccess,
} from '../actions';
import { REQUEST_FORM } from '../actionTypes';
import { getAuthHeaders } from 'api/getAuthHeaders';
import HelloSign from 'hellosign-embedded';
import { getCurrentConnectionStatus } from 'utils/connectionStatus';
import {
  FORM_ALREADY_SIGNED_STATUS,
  SigningOutcome,
  SigningProvider,
} from 'utils/signing/constants';
import { getRequestFormErrorMessage } from 'utils/signing/getRequestFormErrorMessage';
import { summariseErrorBody } from 'utils/logging/summariseErrorBody';
import { openBoldSignEmbed } from 'utils/signing/openBoldSignEmbed';
import { openHelloSignEmbed } from 'utils/signing/openHelloSignEmbed';
import { AppFileType } from 'utils/file/constants';
import { getCaseFile } from 'selectors/files/getCaseFile';

const formTypeToMissingDoc: Record<string, string> = {
  intake: 'Intake Form',
  abn: 'ABN Form',
  lien: 'Lien Form',
};

/**
 * The same three form types, in the vocabulary the file store uses.
 *
 * `formTypeToMissingDoc` above maps to the lead's `missingDocs` labels, which
 *   are lead-scoped display strings. This one maps to the key the attachment
 *   list is normalised under, which is what `pollCaseFilesAfterSigning$` and
 *   `CaseFileControls` look the document up by.
 */
const formTypeToAppFileType: Record<string, AppFileType> = {
  intake: AppFileType.IntakeForm,
  abn: AppFileType.AbnForm,
  lien: AppFileType.LienForm,
};

type RequestFormPayload = {
  client: HelloSign;
  caseId: string;
  leadId: string;
  form_type: string;
  embed: boolean;
};

export const requestForm$: CustomEpic<RequestFormPayload> = (
  action$,
  state$,
  { postApi$ },
) =>
  action$.pipe(
    ofType(REQUEST_FORM),
    // `exhaustMap`, not `switchMap`: while one request is in flight every new
    //   REQUEST_FORM is ignored, which is the semantics of a hammered button.
    //   `switchMap` cancelled the *first* one instead — the POST may already
    //   have reached the server, so it produced a second signature request plus
    //   an orphaned overlay whose outcome was discarded. `concatMap` would be
    //   just as wrong: it queues both and sends both anyway.
    //
    // What this does NOT cover, so nobody assumes the subject is closed:
    //   - a retry after the network drops: the offline queue replays the stored
    //     request, and nothing yet lets the backend collapse it — see below
    //   - a second tab, or a client still running an older build: separate
    //     stores, separate in-flight state, no shared key
    //   - a hung request: there is no axios timeout, so the in-flight state
    //     lasts until the browser gives up on the socket, and Sign stays
    //     disabled until then
    exhaustMap((action: PayloadAction<RequestFormPayload>) => {
      const { caseId, leadId, ...rest } = action.payload;
      const toastId = toast.loading('Requesting form');

      // Captured now, before the request and the embed: on a re-send there is
      //   already an archived document for this type, so the poll that follows
      //   cannot wait for one to *appear*. It waits for this id to be replaced.
      //   See `pollCaseFilesAfterSigning$`.
      const appFileType = formTypeToAppFileType[rest.form_type];
      // `getFiles` reads `state.files.normalized` unguarded, and this sits
      //   inside `exhaustMap` — where a throw would kill the epic and end form
      //   requests for the session. `state.files` is always present in a live
      //   store, so the guard is only for a state shape that is not.
      const previousFileId =
        appFileType && state$.value?.files
          ? getCaseFile(state$.value, { caseId, appFileType })?.fileId
          : undefined;
      return postApi$({
        endpoint: `/cases/${caseId}/send_form`,
        headers: getAuthHeaders(state$.value),
        // No idempotency key yet, deliberately. The agreed design is an
        //   `Idempotency-Key` header minted at the intent layer and stored with
        //   the queued item, so a replay reuses it and both offline queues share
        //   one mechanism. Minting it here — at the moment of transmitting —
        //   would hand out a fresh key on every replay and make the guarantee
        //   decoration, so it waits for the queue rework rather than landing in
        //   the wrong layer first.
        payload: { ...rest, resend: true },
      }).pipe(
        switchMap((result: any) => {
          toast.dismiss(toastId);

          // A non-embedded request (`embed: false`) only confirms the form was
          //   issued for out-of-band signing — the body carries neither
          //   `sign_url` nor `provider`. Nothing was signed here, so there is
          //   no embed to open and no success to report.
          if (!result.data.sign_url) return of(requestFormCancel());

          // Read from each individual response, never cached or derived from
          //   build config: the backend picks the provider per environment, per
          //   form type *and* per sending therapist (a pilot group). Two
          //   requests for the same form type, seconds apart, can legitimately
          //   come back with different providers.
          const openEmbed =
            result.data.provider === SigningProvider.BoldSign
              ? openBoldSignEmbed
              : openHelloSignEmbed;

          return from(openEmbed(result.data.sign_url)).pipe(
            map(outcome =>
              outcome === SigningOutcome.Signed
                ? requestFormSuccess({
                    leadId,
                    missingDoc: formTypeToMissingDoc[rest.form_type],
                    caseId,
                    appFileType,
                    // Read before the embed opened, so it is the document this
                    //   signature is replacing rather than the one it produced.
                    previousFileId,
                  })
                : requestFormCancel(),
            ),
          );
        }),
        // Both actions are dispatched: the signing outcome so the reducer can
        //   clear the signed document from `missingDocs`, then the refetch so
        //   the file list reflects the new document.
        //
        // Clearing `missingDocs` here is optimistic. The in-frame signing event
        //   is a UI cue, not proof of storage — what files the signed PDF
        //   against the case is the provider's `Completed` webhook. A failed
        //   webhook leaves this stale until the lead is refetched, which is the
        //   accepted trade for not showing a freshly signed form as missing.
        switchMap(signingAction =>
          of(signingAction, fetchAppFiles({ caseId })),
        ),
        catchError(err => {
          toastId && toast.dismiss(toastId);
          if (getCurrentConnectionStatus() === 'offline') {
            toast.success(
              'Form request will be sent when connection is back.',
              {
                icon: '✖️',
              },
            );
          }
          if (
            err.code !== 'ERR_CANCELED' &&
            err.response &&
            err?.response?.status !== 401
          ) {
            // The backend names the real cause in the body, and losing that
            //   once cost an afternoon hunting a misconfigured variable while
            //   the provider's answer said the subscription had lapsed. So the
            //   diagnosis is still logged — but summarised rather than verbatim.
            //
            // `console.error` is a Sentry breadcrumb by default and the
            //   breadcrumb carries the raw arguments, so a body we did not
            //   author would leave the device. See `summariseErrorBody`.
            console.error('send_form failed', {
              status: err.response.status,
              body: summariseErrorBody(err.response.data),
            });
            toast.error(getRequestFormErrorMessage(err.response));
          }

          // A `409` means an archived document already exists for this case and
          //   form type — which is the very signal `CaseFileControls` reads to
          //   decide between "Sign" and "Re-send". Getting one therefore proves
          //   the list on screen is stale, so re-reading it is both free
          //   information and the way out: the button becomes "Re-send" and the
          //   therapist has a next move instead of a dead end.
          //
          // Without this the 409 is a lock with no key. It is reachable after a
          //   signature whose archived row landed later than the poll's 30 s
          //   budget in `pollCaseFilesAfterSigning$` — the row exists, the list
          //   does not know, and every further click repeats the same refusal.
          //
          // Today the app sends `resend: true` unconditionally, so this path is
          //   mostly unreachable from here. It stops being groundwork the moment
          //   `resend` becomes conditional, which is why it lands first.
          return err?.response?.status === FORM_ALREADY_SIGNED_STATUS
            ? [requestFormFailure(), fetchAppFiles({ caseId })]
            : [requestFormFailure()];
        }),
      );
    }),
  );
